import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  collection,
  doc,
  endAt,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAt,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

const GOLD = '#9d6f32';
const GOLD_D = '#7a5525';
const DARK = '#1a1a1a';
const MUTED = '#6b6b6b';
const FAINT = '#938b7d';
const BORDER = '#e8e8e8';
const PANEL2 = '#f3f0ea';
const FONT_HEADING = "'Georgia', 'Times New Roman', serif";
const FONT_BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const SQ_METER_TO_SQ_YARD = 1.1959900463;

const toggleSx = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 12.5,
  color: MUTED,
  borderColor: BORDER,
  px: 1.5,
  '&.Mui-selected': { color: '#fff', bgcolor: GOLD, '&:hover': { bgcolor: GOLD_D } },
};

const compactToggleSx = {
  ...toggleSx,
  fontSize: 11.5,
  px: 1,
  py: 0.35,
  minHeight: 28,
};

type ScheduleMode = 'afterFirst' | 'fromStart';
type FeasibilityTab = 'paymentSchedule' | 'landOnly' | 'landPlotting' | 'landBuilding' | 'settings';
type LandAreaUnit = 'sqYard' | 'sqFt' | 'sqMeter' | 'acre' | 'bigha';
type RateUnit = LandAreaUnit;
type LandCheckBasis = '712' | 'fp';
type PlottingDeductionColumn = {
  id: string;
  value: string;
  custom?: boolean;
};
type PlottingScenarioInputs = {
  developmentPerSqYard: string;
  saleableRatePerSqYard: string;
};
type CompareScenario = {
  id: number;
  label: string;
  terms: number;
  installments: number;
  scheduleMode: ScheduleMode;
  rows: PaymentScheduleRow[];
  total: number;
};
type BuildingScenario = {
  id: number;
  label: string;
  name: string;
  tpNumber: string;
  fpNumber: string;
  landArea: string;
  saleablePerYard: string;
  groundCommercial: string;
  firstCommercial: string;
  multiplier: string;
  residentialValue: string;
  commercialGfValue: string;
  commercialFfValue: string;
  constructionValue: string;
};
type SavedBuildingScenario = {
  id: string;
  name: string;
  tpNumber: string;
  fpNumber: string;
  landArea: number;
  fsi: number;
  gfSaleableArea: number;
  ffSaleableArea: number;
  residentialArea: number;
};

function createBuildingScenario(label: string): BuildingScenario {
  return {
    id: Date.now() + Math.random(),
    label,
    name: '',
    tpNumber: '',
    fpNumber: '',
    landArea: '',
    saleablePerYard: '',
    groundCommercial: '',
    firstCommercial: '',
    multiplier: '',
    residentialValue: '',
    commercialGfValue: '',
    commercialFfValue: '',
    constructionValue: '',
  };
}

function parseSavedBuildingScenario(id: string, data: any): SavedBuildingScenario {
  return {
    id,
    name: typeof data.name === 'string' ? data.name : '',
    tpNumber: typeof data.tpNumber === 'string' ? data.tpNumber : '',
    fpNumber: typeof data.fpNumber === 'string' ? data.fpNumber : '',
    landArea: num(data.landArea),
    fsi: num(data.fsi),
    gfSaleableArea: num(data.gfSaleableArea),
    ffSaleableArea: num(data.ffSaleableArea),
    residentialArea: num(data.residentialArea),
  };
}

function buildingScenarioDocId(tpNumber: string, fpNumber: string): string {
  return `${encodeURIComponent(tpNumber.trim())}__${encodeURIComponent(fpNumber.trim())}`;
}

function getBuildingScenarioFigures(scenario: BuildingScenario) {
  const landAreaSqYard = num(scenario.landArea);
  const totalSaleable = landAreaSqYard * num(scenario.saleablePerYard);
  const groundCommercialSft = num(scenario.groundCommercial);
  const firstCommercialSft = num(scenario.firstCommercial);
  const commercialTotal = groundCommercialSft + firstCommercialSft;
  const residentialSaleable = Math.max(totalSaleable - commercialTotal, 0);
  const residentialFigure = residentialSaleable * num(scenario.residentialValue);
  const commercialGfFigure = groundCommercialSft * num(scenario.commercialGfValue);
  const commercialFfFigure = firstCommercialSft * num(scenario.commercialFfValue);
  const projectFigure = residentialFigure + commercialGfFigure + commercialFfFigure;
  const landFigure = landAreaSqYard * num(scenario.multiplier);
  const constructionFigure = totalSaleable * num(scenario.constructionValue);
  const netFigure = projectFigure - landFigure - constructionFigure;
  const netLandRatio = landFigure > 0 ? (netFigure / landFigure) * 100 : 0;
  return {
    id: scenario.id,
    landAreaSqYard,
    totalSaleable,
    groundCommercialSft,
    firstCommercialSft,
    commercialTotal,
    residentialSaleable,
    residentialFigure,
    commercialGfFigure,
    commercialFfFigure,
    projectFigure,
    landFigure,
    constructionFigure,
    netFigure,
    netPerSft: totalSaleable > 0 ? netFigure / totalSaleable : 0,
    netLandRatio,
    hasProjectSnapshotValue: num(scenario.residentialValue) > 0
      || (groundCommercialSft > 0 && num(scenario.commercialGfValue) > 0)
      || (firstCommercialSft > 0 && num(scenario.commercialFfValue) > 0)
      || num(scenario.constructionValue) > 0,
  };
}

export default function OfficeFeasibilityPage() {
  const [activeTab, setActiveTab] = useState<FeasibilityTab>('landBuilding');
  const [date, setDate] = useState(() => formatInputDate(new Date()));
  const [terms, setTerms] = useState('');
  const [installments, setInstallments] = useState('');
  const [landArea, setLandArea] = useState('');
  const [landAreaUnit, setLandAreaUnit] = useState<LandAreaUnit>('sqYard');
  const [landRate, setLandRate] = useState('');
  const [landRateUnit, setLandRateUnit] = useState<RateUnit>('bigha');
  const [landCheckBasis, setLandCheckBasis] = useState<LandCheckBasis>('712');
  const [landRateBasis, setLandRateBasis] = useState<LandCheckBasis>('712');
  const [landOnlyUnitInitialized, setLandOnlyUnitInitialized] = useState(false);
  const [landPlottingBasisInitialized, setLandPlottingBasisInitialized] = useState(false);
  const [bighaSqMeter, setBighaSqMeter] = useState('');
  const [bighaSqMeterDraft, setBighaSqMeterDraft] = useState('');
  const [fpDeduction, setFpDeduction] = useState('40');
  const [fpDeductionDraft, setFpDeductionDraft] = useState('40');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsStatus, setSettingsStatus] = useState('');
  const [multiplier, setMultiplier] = useState('');
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('afterFirst');
  const [installmentPercents, setInstallmentPercents] = useState<string[]>([]);
  const [compareScenarios, setCompareScenarios] = useState<CompareScenario[]>([]);
  const [buildingScenarios, setBuildingScenarios] = useState<BuildingScenario[]>(() => [createBuildingScenario('S1')]);
  const [buildingSaving, setBuildingSaving] = useState<Record<number, boolean>>({});
  const [buildingSaveStatus, setBuildingSaveStatus] = useState<Record<number, string>>({});
  const [buildingNameSearchOptions, setBuildingNameSearchOptions] = useState<Record<number, SavedBuildingScenario[]>>({});
  const [buildingTpSearchOptions, setBuildingTpSearchOptions] = useState<Record<number, SavedBuildingScenario[]>>({});
  const [buildingFpSearchOptions, setBuildingFpSearchOptions] = useState<Record<number, SavedBuildingScenario[]>>({});
  const [buildingLoadedScenarios, setBuildingLoadedScenarios] = useState<Record<number, boolean>>({});
  const [buildingEditingCells, setBuildingEditingCells] = useState<Record<string, boolean>>({});
  const [buildingTableScrolled, setBuildingTableScrolled] = useState(false);
  const [plottingDeductions, setPlottingDeductions] = useState<PlottingDeductionColumn[]>([
    { id: 'default-35', value: '35' },
    { id: 'default-32', value: '32' },
    { id: 'default-30', value: '30' },
  ]);
  const [plottingLegalPercent, setPlottingLegalPercent] = useState('');
  const [plottingScenarioInputs, setPlottingScenarioInputs] = useState<Record<string, PlottingScenarioInputs>>({});

  useEffect(() => {
    const count = Math.floor(num(installments));
    setInstallmentPercents(count > 0 ? defaultPercentInputs(count) : []);
  }, [installments]);

  useEffect(() => {
    if ((activeTab !== 'landOnly' && activeTab !== 'landPlotting') || landOnlyUnitInitialized) return;
    setLandAreaUnit('bigha');
    setLandOnlyUnitInitialized(true);
  }, [activeTab, landOnlyUnitInitialized]);

  useEffect(() => {
    if (activeTab !== 'landPlotting' || landPlottingBasisInitialized) return;
    setLandAreaUnit('sqYard');
    setLandRateUnit('sqYard');
    setLandCheckBasis('fp');
    setLandRateBasis('fp');
    setLandPlottingBasisInitialized(true);
  }, [activeTab, landPlottingBasisInitialized]);

  useEffect(() => {
    let alive = true;
    getDoc(doc(db, 'config', 'feasibilitySettings'))
      .then((snap) => {
        if (!alive) return;
        const data = snap.exists() ? (snap.data() as any) : {};
        const value = num(data?.bighaSqMeter);
        const deduction = data?.fpDeductionPercent === undefined ? 40 : num(data?.fpDeductionPercent);
        if (value > 0) {
          const text = String(value);
          setBighaSqMeter(text);
          setBighaSqMeterDraft(text);
        }
        if (deduction >= 0) {
          const text = String(deduction);
          setFpDeduction(text);
          setFpDeductionDraft(text);
        }
      })
      .catch(() => {
        if (alive) setSettingsStatus('Could not load conversion setting.');
      });
    return () => { alive = false; };
  }, []);

  const saveConversionSettings = async () => {
    const value = num(bighaSqMeterDraft);
    const deduction = num(fpDeductionDraft);
    if (value <= 0) {
      setSettingsStatus('Enter a valid Sq Meter value for 1 Bigha.');
      return;
    }
    if (deduction < 0 || deduction >= 100) {
      setSettingsStatus('Enter FP deduction between 0 and 99.');
      return;
    }
    setSettingsSaving(true);
    setSettingsStatus('');
    try {
      await setDoc(doc(db, 'config', 'feasibilitySettings'), {
        bighaSqMeter: value,
        fpDeductionPercent: deduction,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setBighaSqMeter(String(value));
      setFpDeduction(String(deduction));
      setSettingsStatus('Conversion setting saved.');
    } catch {
      setSettingsStatus('Failed to save conversion setting.');
    } finally {
      setSettingsSaving(false);
    }
  };

  const schedule = useMemo(() => {
    return buildPaymentSchedule({
      date,
      terms: num(terms),
      installments: Math.floor(num(installments)),
      landArea: convertToSqYard(num(landArea), landAreaUnit, num(bighaSqMeter)),
      multiplier: num(multiplier),
      scheduleMode,
      percents: installmentPercents.map(num),
    });
  }, [bighaSqMeter, date, installmentPercents, installments, landArea, landAreaUnit, multiplier, scheduleMode, terms]);

  const convertedSqYard = useMemo(
    () => convertToSqYard(num(landArea), landAreaUnit, num(bighaSqMeter)),
    [bighaSqMeter, landArea, landAreaUnit]
  );

  const landOnlyFigures = useMemo(() => {
    const deduction = Math.max(0, Math.min(num(fpDeduction), 99));
    const fpFactor = (100 - deduction) / 100;
    const inputArea = convertedSqYard;
    const fpArea = landCheckBasis === 'fp' ? inputArea : inputArea * fpFactor;
    const area712 = landCheckBasis === 'fp' && fpFactor > 0 ? inputArea / fpFactor : inputArea;
    const unitSqYard = getUnitSqYard(landRateUnit, num(bighaSqMeter));
    const basisArea = landRateBasis === 'fp' ? fpArea : area712;
    const ratePerSqYardOnBasis = unitSqYard > 0 ? num(landRate) / unitSqYard : 0;
    const landFigure = basisArea * ratePerSqYardOnBasis;
    const ratePerSqYard712 = area712 > 0 ? landFigure / area712 : 0;
    const ratePerSqYardFp = fpArea > 0 ? landFigure / fpArea : 0;
    const scheduleArea = landCheckBasis === 'fp' ? fpArea : area712;
    const scheduleMultiplier = landCheckBasis === 'fp' ? ratePerSqYardFp : ratePerSqYard712;
    return {
      area712,
      fpArea,
      deduction,
      ratePerSqYard712,
      ratePerSqYardFp,
      landFigure,
      scheduleArea,
      scheduleMultiplier,
    };
  }, [bighaSqMeter, convertedSqYard, fpDeduction, landCheckBasis, landRate, landRateBasis, landRateUnit]);

  const landOnlySchedule = useMemo(() => {
    return buildPaymentSchedule({
      date,
      terms: num(terms),
      installments: Math.floor(num(installments)),
      landArea: landOnlyFigures.scheduleArea,
      multiplier: landOnlyFigures.scheduleMultiplier,
      scheduleMode,
      percents: installmentPercents.map(num),
    });
  }, [date, installmentPercents, installments, landOnlyFigures.scheduleArea, landOnlyFigures.scheduleMultiplier, scheduleMode, terms]);

  const buildingSchedule = useMemo(() => {
    const firstScenario = buildingScenarios[0];
    return buildPaymentSchedule({
      date,
      terms: num(terms),
      installments: Math.floor(num(installments)),
      landArea: num(firstScenario?.landArea),
      multiplier: num(firstScenario?.multiplier),
      scheduleMode,
      percents: installmentPercents.map(num),
    });
  }, [buildingScenarios, date, installmentPercents, installments, scheduleMode, terms]);

  const buildingScenarioFigures = buildingScenarios.map((scenario) => (
    getBuildingScenarioFigures(scenario)
  ));
  const hasAnyGroundCommercial = buildingScenarioFigures.some((figures) => figures.groundCommercialSft > 0);
  const hasAnyFirstCommercial = buildingScenarioFigures.some((figures) => figures.firstCommercialSft > 0);
  const hasResidentialSaleable = buildingScenarioFigures.some((figures) => figures.residentialSaleable > 0);
  const hasTotalSaleable = buildingScenarioFigures.some((figures) => figures.totalSaleable > 0);
  const hasResidentialFigure = buildingScenarioFigures.some((figures) => figures.residentialFigure > 0);
  const hasCommercialGfFigure = buildingScenarioFigures.some((figures) => figures.commercialGfFigure > 0);
  const hasCommercialFfFigure = buildingScenarioFigures.some((figures) => figures.commercialFfFigure > 0);
  const hasProjectFigure = buildingScenarioFigures.some((figures) => figures.projectFigure > 0);
  const hasLandFigure = buildingScenarioFigures.some((figures) => figures.landFigure > 0);
  const hasConstructionFigure = buildingScenarioFigures.some((figures) => figures.constructionFigure > 0);
  const hasNetPerSft = buildingScenarioFigures.some((figures) => figures.netPerSft > 0);
  const hasNetFigure = buildingScenarioFigures.some((figures) => figures.netFigure > 0);
  const hasNetLandRatio = buildingScenarioFigures.some((figures) => figures.netLandRatio > 0);
  const commercialWarning = buildingScenarioFigures.some((figures) => figures.commercialTotal > figures.totalSaleable)
    ? 'Commercial saleable space is more than total saleable area in one or more scenarios.'
    : '';

  const addBuildingScenario = () => {
    setBuildingScenarios((prev) => ([
      ...prev,
      createBuildingScenario(`S${prev.length + 1}`),
    ]));
  };

  const updateBuildingScenario = (id: number, field: keyof Omit<BuildingScenario, 'id' | 'label'>, value: string) => {
    setBuildingScenarios((prev) => prev.map((scenario) => (
      scenario.id === id ? { ...scenario, [field]: value } : scenario
    )));
    if (field === 'tpNumber') {
      setBuildingTpSearchOptions((prev) => ({ ...prev, [id]: [] }));
      setBuildingFpSearchOptions((prev) => ({ ...prev, [id]: [] }));
    }
  };

  const removeBuildingScenario = (id: number) => {
    setBuildingScenarios((prev) => (prev.length > 1 ? prev.filter((scenario) => scenario.id !== id) : prev));
  };

  const buildingCellKey = (scenarioId: number, field: keyof Omit<BuildingScenario, 'id' | 'label'>) => `${scenarioId}:${field}`;

  const shouldShowLoadedBuildingValue = (scenario: BuildingScenario, field: keyof Omit<BuildingScenario, 'id' | 'label'>) => (
    Boolean(buildingLoadedScenarios[scenario.id]) && !buildingEditingCells[buildingCellKey(scenario.id, field)]
  );

  const editBuildingCell = (scenarioId: number, field: keyof Omit<BuildingScenario, 'id' | 'label'>) => {
    setBuildingEditingCells((prev) => ({ ...prev, [buildingCellKey(scenarioId, field)]: true }));
  };

  const searchSavedBuildingScenariosByName = async (scenarioId: number, text: string) => {
    const term = text.trim();
    if (term.length < 2) {
      setBuildingNameSearchOptions((prev) => ({ ...prev, [scenarioId]: [] }));
      return;
    }

    try {
      const snap = await getDocs(query(
        collection(db, 'feasibility_land_building'),
        orderBy('name'),
        limit(80)
      ));
      const lowerTerm = term.toLowerCase();
      const matches: SavedBuildingScenario[] = [];
      snap.forEach((item) => {
        const saved = parseSavedBuildingScenario(item.id, item.data());
        if (saved.name.toLowerCase().includes(lowerTerm)) matches.push(saved);
      });
      matches.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
      setBuildingNameSearchOptions((prev) => ({ ...prev, [scenarioId]: matches.slice(0, 8) }));
    } catch {
      setBuildingNameSearchOptions((prev) => ({ ...prev, [scenarioId]: [] }));
    }
  };

  const searchSavedBuildingScenariosByTp = async (scenarioId: number, text: string) => {
    const term = text.trim();
    if (term.length < 1) {
      setBuildingTpSearchOptions((prev) => ({ ...prev, [scenarioId]: [] }));
      return;
    }

    try {
      const snap = await getDocs(query(
        collection(db, 'feasibility_land_building'),
        orderBy('tpNumber'),
        startAt(term),
        endAt(`${term}\uf8ff`),
        limit(30)
      ));
      const byTp = new Map<string, SavedBuildingScenario>();
      snap.forEach((item) => {
        const saved = parseSavedBuildingScenario(item.id, item.data());
        if (saved.tpNumber && !byTp.has(saved.tpNumber)) byTp.set(saved.tpNumber, saved);
      });
      setBuildingTpSearchOptions((prev) => ({ ...prev, [scenarioId]: Array.from(byTp.values()).slice(0, 8) }));
    } catch {
      setBuildingTpSearchOptions((prev) => ({ ...prev, [scenarioId]: [] }));
    }
  };

  const searchSavedBuildingScenariosByFp = async (scenarioId: number, tpNumber: string, text: string) => {
    const tp = tpNumber.trim();
    const term = text.trim();
    if (!tp || term.length < 1) {
      setBuildingFpSearchOptions((prev) => ({ ...prev, [scenarioId]: [] }));
      return;
    }

    try {
      const snap = await getDocs(query(
        collection(db, 'feasibility_land_building'),
        where('tpNumber', '==', tp),
        limit(30)
      ));
      const lowerTerm = term.toLowerCase();
      const matches: SavedBuildingScenario[] = [];
      snap.forEach((item) => {
        const saved = parseSavedBuildingScenario(item.id, item.data());
        if (saved.fpNumber.toLowerCase().startsWith(lowerTerm)) matches.push(saved);
      });
      matches.sort((a, b) => a.fpNumber.localeCompare(b.fpNumber, undefined, { numeric: true }));
      setBuildingFpSearchOptions((prev) => ({ ...prev, [scenarioId]: matches.slice(0, 8) }));
    } catch {
      setBuildingFpSearchOptions((prev) => ({ ...prev, [scenarioId]: [] }));
    }
  };

  const fillBuildingScenarioFromSaved = (scenarioId: number, saved: SavedBuildingScenario) => {
    setBuildingScenarios((prev) => prev.map((scenario) => (
      scenario.id === scenarioId
        ? {
          ...scenario,
          name: saved.name,
          tpNumber: saved.tpNumber,
          fpNumber: saved.fpNumber,
          landArea: String(saved.landArea || ''),
          saleablePerYard: String(saved.fsi || ''),
          groundCommercial: String(saved.gfSaleableArea || ''),
          firstCommercial: String(saved.ffSaleableArea || ''),
        }
        : scenario
    )));
    setBuildingNameSearchOptions((prev) => ({ ...prev, [scenarioId]: [] }));
    setBuildingTpSearchOptions((prev) => ({ ...prev, [scenarioId]: [] }));
    setBuildingFpSearchOptions((prev) => ({ ...prev, [scenarioId]: [] }));
    setBuildingLoadedScenarios((prev) => ({ ...prev, [scenarioId]: true }));
    setBuildingEditingCells((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${scenarioId}:`)) delete next[key];
      });
      return next;
    });
    setBuildingSaveStatus((prev) => ({ ...prev, [scenarioId]: 'Loaded.' }));
  };

  const fillBuildingScenarioTp = (scenarioId: number, saved: SavedBuildingScenario) => {
    setBuildingScenarios((prev) => prev.map((scenario) => (
      scenario.id === scenarioId ? { ...scenario, tpNumber: saved.tpNumber, fpNumber: '' } : scenario
    )));
    setBuildingTpSearchOptions((prev) => ({ ...prev, [scenarioId]: [] }));
    setBuildingFpSearchOptions((prev) => ({ ...prev, [scenarioId]: [] }));
  };

  const saveBuildingScenario = async (scenario: BuildingScenario) => {
    const name = scenario.name.trim();
    const tpNumber = scenario.tpNumber.trim();
    const fpNumber = scenario.fpNumber.trim();
    if (!name || !tpNumber || !fpNumber) {
      setBuildingSaveStatus((prev) => ({
        ...prev,
        [scenario.id]: 'Enter Name, TP Number, and FP Number.',
      }));
      return;
    }

    const figures = getBuildingScenarioFigures(scenario);
    const allowedRow = {
      name,
      tpNumber,
      fpNumber,
      landArea: num(scenario.landArea),
      fsi: num(scenario.saleablePerYard),
      gfSaleableArea: num(scenario.groundCommercial),
      ffSaleableArea: num(scenario.firstCommercial),
      residentialArea: figures.residentialSaleable,
    };

    setBuildingSaving((prev) => ({ ...prev, [scenario.id]: true }));
    setBuildingSaveStatus((prev) => ({ ...prev, [scenario.id]: '' }));
    try {
      const ref = doc(db, 'feasibility_land_building', buildingScenarioDocId(tpNumber, fpNumber));
      const existing = await getDoc(ref);
      await setDoc(ref, allowedRow);
      setBuildingSaveStatus((prev) => ({ ...prev, [scenario.id]: existing.exists() ? 'Updated.' : 'Saved.' }));
    } catch {
      setBuildingSaveStatus((prev) => ({ ...prev, [scenario.id]: 'Could not save.' }));
    } finally {
      setBuildingSaving((prev) => ({ ...prev, [scenario.id]: false }));
    }
  };

  const addPlottingDeduction = () => {
    setPlottingDeductions((prev) => ([
      ...prev,
      { id: `custom-${Date.now()}-${Math.random()}`, value: '', custom: true },
    ]));
  };

  const updatePlottingDeduction = (id: string, value: string) => {
    setPlottingDeductions((prev) => prev.map((item) => (
      item.id === id ? { ...item, value } : item
    )));
  };

  const removePlottingDeduction = (id: string) => {
    setPlottingDeductions((prev) => prev.filter((item) => item.id !== id));
    setPlottingScenarioInputs((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const updatePlottingScenarioInput = (id: string, field: keyof PlottingScenarioInputs, value: string) => {
    const firstColumnId = plottingDeductions[0]?.id;
    setPlottingScenarioInputs((prev) => {
      const previousFirstValue = firstColumnId ? prev[firstColumnId]?.[field] || '' : '';
      const next = {
        ...prev,
        [id]: {
          developmentPerSqYard: prev[id]?.developmentPerSqYard || '',
          saleableRatePerSqYard: prev[id]?.saleableRatePerSqYard || '',
          [field]: value,
        },
      };
      if (id === firstColumnId) {
        plottingDeductions.slice(1).forEach((deduction) => {
          const current = next[deduction.id] || {
            developmentPerSqYard: '',
            saleableRatePerSqYard: '',
          };
          if (!current[field] || current[field] === previousFirstValue) {
            next[deduction.id] = {
              ...current,
              [field]: value,
            };
          }
        });
      }
      return next;
    });
  };

  const addToCompare = () => {
    if (!schedule.rows.length) return;
    setCompareScenarios((prev) => ([
      ...prev,
      {
        id: Date.now(),
        label: `${schedule.terms}M / ${schedule.installments}`,
        terms: schedule.terms,
        installments: schedule.installments,
        scheduleMode,
        rows: schedule.rows.map((row) => ({ ...row, date: new Date(row.date) })),
        total: schedule.totalAmount,
      },
    ]));
  };

  const paymentInputs = (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(6,1fr)' }, gap: 1.5, mb: 2 }}>
        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          size="small"
          fullWidth
          autoComplete="off"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Terms"
          value={formatInputNumber(terms)}
          onChange={(e) => setTerms(cleanNumberInput(e.target.value))}
          type="text"
          size="small"
          fullWidth
          autoComplete="off"
          inputProps={{ inputMode: 'decimal' }}
          InputProps={{ endAdornment: <InputAdornment position="end">months</InputAdornment> }}
        />
        <TextField
          label="Installment"
          value={formatInputNumber(installments)}
          onChange={(e) => setInstallments(cleanNumberInput(e.target.value))}
          type="text"
          size="small"
          fullWidth
          autoComplete="off"
          inputProps={{ inputMode: 'decimal' }}
        />
        <TextField
          label="Land Area"
          value={formatInputNumber(landArea)}
          onChange={(e) => setLandArea(cleanNumberInput(e.target.value))}
          type="text"
          size="small"
          fullWidth
          autoComplete="off"
          inputProps={{ inputMode: 'decimal' }}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Unit</InputLabel>
          <Select
            label="Unit"
            value={landAreaUnit}
            onChange={(e) => setLandAreaUnit(e.target.value as LandAreaUnit)}
          >
            <MenuItem value="sqYard">Sq Yard</MenuItem>
            <MenuItem value="sqFt">Sq Ft</MenuItem>
            <MenuItem value="sqMeter">Sq Meter</MenuItem>
            <MenuItem value="acre">Acre</MenuItem>
            <MenuItem value="bigha">Bigha</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Multiplier"
          value={formatInputNumber(multiplier)}
          onChange={(e) => setMultiplier(cleanNumberInput(e.target.value))}
          type="text"
          size="small"
          fullWidth
          autoComplete="off"
          inputProps={{ inputMode: 'decimal' }}
        />
      </Box>

      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} mb={2}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: FAINT, textTransform: 'uppercase', letterSpacing: '.5px' }}>
          Spacing
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={scheduleMode}
          onChange={(_, value) => value && setScheduleMode(value)}
        >
          <ToggleButton value="afterFirst" sx={compactToggleSx}>After 1st</ToggleButton>
          <ToggleButton value="fromStart" sx={compactToggleSx}>From Start</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </>
  );

  const landBuildingInputs = (
    <>
      <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 16, color: DARK, mb: 1 }}>
        Scenario Comparison
      </Typography>

      <>
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', mb: 2, width: 'fit-content', maxWidth: '100%' }}>
            <Box
              onScroll={(event) => setBuildingTableScrolled(event.currentTarget.scrollTop > 0)}
              sx={{ overflow: 'auto', maxWidth: '100%', maxHeight: { xs: 'calc(100vh - 190px)', sm: 'calc(100vh - 180px)' } }}
            >
              <Box component="table" sx={{ borderCollapse: 'collapse', width: 'auto', tableLayout: 'fixed' }}>
                <Box component="colgroup">
                  <Box component="col" sx={{ width: { xs: 118, sm: 300 } }} />
                  {buildingScenarios.map((scenario) => (
                    <Box component="col" key={scenario.id} sx={{ width: { xs: 220, sm: 250 } }} />
                  ))}
                </Box>
                <Box component="thead">
                  <Box component="tr">
                    <Box component="th" sx={{ ...headTh, ...stickyFirstColumnSx, textAlign: 'left', bgcolor: PANEL2 }}>Particular</Box>
                    {buildingScenarios.map((scenario, index) => (
                    <Box component="th" sx={headTh} key={scenario.id}>
                        <Stack direction="column" alignItems="flex-end" gap={0.35}>
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
                            <Box component="span">{scenario.label}</Box>
                            <IconButton
                              size="small"
                              onClick={() => saveBuildingScenario(scenario)}
                              disabled={Boolean(buildingSaving[scenario.id])}
                              aria-label={`Save ${scenario.label}`}
                              title={`Save ${scenario.label}`}
                              sx={{ width: 22, height: 22, color: GOLD_D, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}
                            >
                              <SaveOutlinedIcon fontSize="inherit" />
                            </IconButton>
                            {index === 0 && (
                              <IconButton
                                size="small"
                                onClick={addBuildingScenario}
                                aria-label="Add scenario"
                                title="Add scenario"
                                sx={{ width: 22, height: 22, color: GOLD_D, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}
                              >
                                <AddOutlinedIcon fontSize="inherit" />
                              </IconButton>
                            )}
                            {buildingScenarios.length > 1 && (
                              <IconButton
                                size="small"
                                onClick={() => removeBuildingScenario(scenario.id)}
                                aria-label={`Remove ${scenario.label}`}
                                title={`Remove ${scenario.label}`}
                                sx={{ width: 22, height: 22, color: '#c1372c' }}
                              >
                                <DeleteOutlineIcon fontSize="inherit" />
                              </IconButton>
                            )}
                          </Stack>
                          {buildingSaveStatus[scenario.id] && (
                            <Box
                              component="span"
                              sx={{
                                color: buildingSaveStatus[scenario.id] === 'Saved.' ? GOLD_D : MUTED,
                                fontSize: 9.5,
                                fontWeight: 700,
                                textTransform: 'none',
                                letterSpacing: 0,
                                whiteSpace: 'normal',
                                lineHeight: 1.15,
                              }}
                            >
                              {buildingSaveStatus[scenario.id]}
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  <SnapshotSectionRow label="Land Details" colSpan={buildingScenarios.length + 1} />
                  <SnapshotRow
                    label="Name"
                    calculation=""
                    sticky
                    stickyActive={buildingTableScrolled}
                    values={buildingScenarios.map((scenario, scenarioIndex) => (
                      shouldShowLoadedBuildingValue(scenario, 'name') ? (
                        <LoadedScenarioValue key={scenario.id} onClick={() => editBuildingCell(scenario.id, 'name')}>
                          {scenario.name}
                        </LoadedScenarioValue>
                      ) : (
                        <ScenarioNameSearchField
                          key={scenario.id}
                          value={scenario.name}
                          onChange={(value) => updateBuildingScenario(scenario.id, 'name', value)}
                          options={buildingNameSearchOptions[scenario.id] || []}
                          onSearch={(value) => searchSavedBuildingScenariosByName(scenario.id, value)}
                          onSelect={(saved) => fillBuildingScenarioFromSaved(scenario.id, saved)}
                          scenarioIndex={scenarioIndex}
                          rowOrder={-2}
                        />
                      )
                    ))}
                  />
                  <SnapshotRow
                    label="TP Number"
                    calculation=""
                    values={buildingScenarios.map((scenario, scenarioIndex) => (
                      shouldShowLoadedBuildingValue(scenario, 'tpNumber') ? (
                        <LoadedScenarioValue key={scenario.id} onClick={() => editBuildingCell(scenario.id, 'tpNumber')}>
                          {scenario.tpNumber}
                        </LoadedScenarioValue>
                      ) : (
                        <ScenarioNameSearchField
                          key={scenario.id}
                          value={scenario.tpNumber}
                          onChange={(value) => updateBuildingScenario(scenario.id, 'tpNumber', value)}
                          options={buildingTpSearchOptions[scenario.id] || []}
                          onSearch={(value) => searchSavedBuildingScenariosByTp(scenario.id, value)}
                          onSelect={(saved) => fillBuildingScenarioTp(scenario.id, saved)}
                          scenarioIndex={scenarioIndex}
                          rowOrder={-1}
                        />
                      )
                    ))}
                  />
                  <SnapshotRow
                    label="FP Number"
                    calculation=""
                    values={buildingScenarios.map((scenario, scenarioIndex) => (
                      shouldShowLoadedBuildingValue(scenario, 'fpNumber') ? (
                        <LoadedScenarioValue key={scenario.id} onClick={() => editBuildingCell(scenario.id, 'fpNumber')}>
                          {scenario.fpNumber}
                        </LoadedScenarioValue>
                      ) : (
                        <ScenarioNameSearchField
                          key={scenario.id}
                          value={scenario.fpNumber}
                          onChange={(value) => updateBuildingScenario(scenario.id, 'fpNumber', value)}
                          options={buildingFpSearchOptions[scenario.id] || []}
                          onSearch={(value) => searchSavedBuildingScenariosByFp(scenario.id, scenario.tpNumber, value)}
                          onSelect={(saved) => fillBuildingScenarioFromSaved(scenario.id, saved)}
                          scenarioIndex={scenarioIndex}
                          rowOrder={0}
                        />
                      )
                    ))}
                  />
                  <SnapshotRow
                    label="Land Area"
                    calculation=""
                    values={buildingScenarios.map((scenario, scenarioIndex) => (
                      shouldShowLoadedBuildingValue(scenario, 'landArea') ? (
                        <LoadedScenarioValue key={scenario.id} onClick={() => editBuildingCell(scenario.id, 'landArea')}>
                          <FigureWithUnit value={fmtIN(num(scenario.landArea))} unit="Sq Yard" />
                        </LoadedScenarioValue>
                      ) : (
                        <ScenarioNumberField
                          key={scenario.id}
                          value={scenario.landArea}
                          onChange={(value) => updateBuildingScenario(scenario.id, 'landArea', value)}
                          unit="Sq Yard"
                          scenarioIndex={scenarioIndex}
                          rowOrder={1}
                        />
                      )
                    ))}
                  />
                  <SnapshotSectionRow label="Saleable Area" colSpan={buildingScenarios.length + 1} />
                  <SnapshotRow
                    label="FSI"
                    calculation=""
                    values={buildingScenarios.map((scenario, scenarioIndex) => (
                      shouldShowLoadedBuildingValue(scenario, 'saleablePerYard') ? (
                        <LoadedScenarioValue key={scenario.id} onClick={() => editBuildingCell(scenario.id, 'saleablePerYard')}>
                          {fmtIN(num(scenario.saleablePerYard))}
                        </LoadedScenarioValue>
                      ) : (
                        <ScenarioNumberField
                          key={scenario.id}
                          value={scenario.saleablePerYard}
                          onChange={(value) => updateBuildingScenario(scenario.id, 'saleablePerYard', value)}
                          scenarioIndex={scenarioIndex}
                          rowOrder={2}
                        />
                      )
                    ))}
                  />
                  {hasResidentialSaleable && (
                    <SnapshotRow
                      label="Residential Saleable"
                      calculation={<CalculationText parts={['Total Saleable', '-', 'Commercial GF', '-', 'Commercial FF']} />}
                      values={buildingScenarioFigures.map((figures) => <FigureWithUnit key={figures.id} value={fmtIN(figures.residentialSaleable)} unit="Sq Ft" />)}
                    />
                  )}
                  <SnapshotRow
                    label="Commercial GF Saleable"
                    calculation=""
                    values={buildingScenarios.map((scenario, scenarioIndex) => (
                      shouldShowLoadedBuildingValue(scenario, 'groundCommercial') ? (
                        <LoadedScenarioValue key={scenario.id} onClick={() => editBuildingCell(scenario.id, 'groundCommercial')}>
                          <FigureWithUnit value={fmtIN(num(scenario.groundCommercial))} unit="Sq Ft" />
                        </LoadedScenarioValue>
                      ) : (
                        <ScenarioNumberField
                          key={scenario.id}
                          value={scenario.groundCommercial}
                          onChange={(value) => updateBuildingScenario(scenario.id, 'groundCommercial', value)}
                          unit="Sq Ft"
                          scenarioIndex={scenarioIndex}
                          rowOrder={3}
                        />
                      )
                    ))}
                  />
                  <SnapshotRow
                    label="Commercial FF Saleable"
                    calculation=""
                    values={buildingScenarios.map((scenario, scenarioIndex) => (
                      shouldShowLoadedBuildingValue(scenario, 'firstCommercial') ? (
                        <LoadedScenarioValue key={scenario.id} onClick={() => editBuildingCell(scenario.id, 'firstCommercial')}>
                          <FigureWithUnit value={fmtIN(num(scenario.firstCommercial))} unit="Sq Ft" />
                        </LoadedScenarioValue>
                      ) : (
                        <ScenarioNumberField
                          key={scenario.id}
                          value={scenario.firstCommercial}
                          onChange={(value) => updateBuildingScenario(scenario.id, 'firstCommercial', value)}
                          unit="Sq Ft"
                          scenarioIndex={scenarioIndex}
                          rowOrder={4}
                        />
                      )
                    ))}
                  />
                  {hasTotalSaleable && (
                    <SnapshotRow
                      label="Total Saleable"
                      calculation={<CalculationText parts={['Land Area', 'x', 'FSI']} />}
                      values={buildingScenarioFigures.map((figures) => <FigureWithUnit key={figures.id} value={fmtIN(figures.totalSaleable)} unit="Sq Ft" />)}
                    />
                  )}
                  <SnapshotSectionRow label="Value" colSpan={buildingScenarios.length + 1} />
                  <SnapshotRow
                    label="Residential Value / Sq Ft"
                    calculation=""
                    values={buildingScenarios.map((scenario, scenarioIndex) => (
                      <ScenarioNumberField
                        key={scenario.id}
                        value={scenario.residentialValue}
                        onChange={(value) => updateBuildingScenario(scenario.id, 'residentialValue', value)}
                        scenarioIndex={scenarioIndex}
                        rowOrder={5}
                      />
                    ))}
                  />
                  {hasResidentialFigure && (
                    <SnapshotRow
                      label="Residential Figure"
                      calculation={<CalculationText parts={['Residential Saleable', 'x', 'Residential Value / Sq Ft']} />}
                      values={buildingScenarioFigures.map((figures) => fmtIN(figures.residentialFigure))}
                    />
                  )}
                  {hasAnyGroundCommercial && (
                    <>
                      <SnapshotRow
                        label="Commercial GF Value / Sq Ft"
                        calculation=""
                        values={buildingScenarios.map((scenario, scenarioIndex) => (
                          <ScenarioNumberField
                            key={scenario.id}
                            value={scenario.commercialGfValue}
                            onChange={(value) => updateBuildingScenario(scenario.id, 'commercialGfValue', value)}
                            scenarioIndex={scenarioIndex}
                            rowOrder={6}
                          />
                        ))}
                      />
                      {hasCommercialGfFigure && (
                        <SnapshotRow
                          label="Commercial GF Figure"
                          calculation={<CalculationText parts={['Commercial GF Saleable', 'x', 'Commercial GF Value / Sq Ft']} />}
                          values={buildingScenarioFigures.map((figures) => fmtIN(figures.commercialGfFigure))}
                        />
                      )}
                    </>
                  )}
                  {hasAnyFirstCommercial && (
                    <>
                      <SnapshotRow
                        label="Commercial FF Value / Sq Ft"
                        calculation=""
                        values={buildingScenarios.map((scenario, scenarioIndex) => (
                          <ScenarioNumberField
                            key={scenario.id}
                            value={scenario.commercialFfValue}
                            onChange={(value) => updateBuildingScenario(scenario.id, 'commercialFfValue', value)}
                            scenarioIndex={scenarioIndex}
                            rowOrder={7}
                          />
                        ))}
                      />
                      {hasCommercialFfFigure && (
                        <SnapshotRow
                          label="Commercial FF Figure"
                          calculation={<CalculationText parts={['Commercial FF Saleable', 'x', 'Commercial FF Value / Sq Ft']} />}
                          values={buildingScenarioFigures.map((figures) => fmtIN(figures.commercialFfFigure))}
                        />
                      )}
                    </>
                  )}
                  {hasProjectFigure && (
                    <SnapshotRow
                      label="Project Figure"
                      calculation=""
                      values={buildingScenarioFigures.map((figures) => fmtIN(figures.projectFigure))}
                    />
                  )}
                  <SnapshotSectionRow label="Cost" colSpan={buildingScenarios.length + 1} />
                  <SnapshotRow
                    label="Multiplier"
                    calculation=""
                    values={buildingScenarios.map((scenario, scenarioIndex) => (
                      <ScenarioNumberField
                        key={scenario.id}
                        value={scenario.multiplier}
                        onChange={(value) => updateBuildingScenario(scenario.id, 'multiplier', value)}
                        scenarioIndex={scenarioIndex}
                        rowOrder={8}
                      />
                    ))}
                  />
                  {hasLandFigure && (
                    <SnapshotRow
                      label="Land"
                      calculation={<CalculationText parts={['Land Area', 'x', 'Multiplier']} />}
                      values={buildingScenarioFigures.map((figures) => fmtIN(figures.landFigure))}
                    />
                  )}
                  <SnapshotRow
                    label="Construction / Sq Ft"
                    calculation=""
                    values={buildingScenarios.map((scenario, scenarioIndex) => (
                      <ScenarioNumberField
                        key={scenario.id}
                        value={scenario.constructionValue}
                        onChange={(value) => updateBuildingScenario(scenario.id, 'constructionValue', value)}
                        scenarioIndex={scenarioIndex}
                        rowOrder={9}
                      />
                    ))}
                  />
                  {hasConstructionFigure && (
                    <SnapshotRow
                      label="Construction"
                      calculation={<CalculationText parts={['Total Saleable', 'x', 'Construction / Sq Ft']} />}
                      values={buildingScenarioFigures.map((figures) => fmtIN(figures.constructionFigure))}
                    />
                  )}
                  {(hasNetPerSft || hasNetFigure) && (
                    <>
                      <SnapshotSectionRow label="Result" colSpan={buildingScenarios.length + 1} />
                      {hasNetPerSft && (
                      <SnapshotRow
                        label="Net / Sq Ft"
                        calculation={<CalculationText parts={['Net Figure', '/', 'Total Saleable']} />}
                        values={buildingScenarioFigures.map((figures) => fmtIN(figures.netPerSft))}
                      />
                      )}
                      {hasNetFigure && (
                      <SnapshotRow
                        label="Net Figure"
                        calculation=""
                        values={buildingScenarioFigures.map((figures) => fmtIN(figures.netFigure))}
                        strong
                      />
                      )}
                      {hasNetLandRatio && (
                      <SnapshotRow
                        label="Net / Land"
                        calculation={<CalculationText parts={['Net Figure', '/', 'Land']} />}
                        values={buildingScenarioFigures.map((figures) => fmtPercent(figures.netLandRatio))}
                      />
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

        {commercialWarning && (
          <Alert severity="warning" sx={{ mb: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff8ec', color: DARK }}>
            {commercialWarning}
          </Alert>
        )}

      </>

      <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 16, color: DARK, mb: 1 }}>
        Payment Schedule
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: 1.5, mb: 2 }}>
        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          size="small"
          fullWidth
          autoComplete="off"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Terms"
          value={formatInputNumber(terms)}
          onChange={(e) => setTerms(cleanNumberInput(e.target.value))}
          type="text"
          size="small"
          fullWidth
          autoComplete="off"
          inputProps={{ inputMode: 'decimal' }}
          InputProps={{ endAdornment: <InputAdornment position="end">months</InputAdornment> }}
        />
        <TextField
          label="Installment"
          value={formatInputNumber(installments)}
          onChange={(e) => setInstallments(cleanNumberInput(e.target.value))}
          type="text"
          size="small"
          fullWidth
          autoComplete="off"
          inputProps={{ inputMode: 'decimal' }}
        />
      </Box>

      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} mb={2}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: FAINT, textTransform: 'uppercase', letterSpacing: '.5px' }}>
          Spacing
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={scheduleMode}
          onChange={(_, value) => value && setScheduleMode(value)}
        >
          <ToggleButton value="afterFirst" sx={compactToggleSx}>After 1st</ToggleButton>
          <ToggleButton value="fromStart" sx={compactToggleSx}>From Start</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {buildingSchedule.warning && (
        <Alert severity="warning" sx={{ mb: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff8ec', color: DARK }}>
          {buildingSchedule.warning}
        </Alert>
      )}

      {buildingSchedule.rows.length > 0 && (
        <>
          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 680 }}>
                <Box component="thead">
                  <Box component="tr">
                    <Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Installment</Box>
                    <Box component="th" sx={headTh}>Installment Date</Box>
                    <Box component="th" sx={headTh}>Installment %</Box>
                    <Box component="th" sx={headTh}>Installment</Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {buildingSchedule.rows.map((row) => (
                    <Box component="tr" key={row.installment} sx={{ '&:hover td': { bgcolor: 'rgba(157,111,50,0.08)' } }}>
                      <Box component="td" sx={{ ...bodyTd, textAlign: 'left', fontWeight: 600 }}>{row.installment}</Box>
                      <Box component="td" sx={bodyTd}>{formatDate(row.date)}</Box>
                      <Box component="td" sx={bodyTd}>
                        <TextField
                          value={formatInputNumber(installmentPercents[row.installment - 1] ?? '')}
                          onChange={(e) => {
                            const value = cleanNumberInput(e.target.value);
                            setInstallmentPercents((prev) => prev.map((pct, index) => (
                              index === row.installment - 1 ? value : pct
                            )));
                          }}
                          type="text"
                          size="small"
                          autoComplete="off"
                          inputProps={{ inputMode: 'decimal' }}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          sx={{ width: 110 }}
                        />
                      </Box>
                      <Box component="td" sx={{ ...bodyTd, fontWeight: 700, color: GOLD_D }}>{fmtIN(row.amount)}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  );

  const landOnlyInputs = (
    <>
      <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 16, color: DARK, mb: 1 }}>
        Feasibility Check
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, gap: 1.5, mb: 2 }}>
        <TextField
          label="Land Area"
          value={formatInputNumber(landArea)}
          onChange={(e) => setLandArea(cleanNumberInput(e.target.value))}
          type="text"
          size="small"
          fullWidth
          autoComplete="off"
          inputProps={{ inputMode: 'decimal' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ m: 0, mr: -1.75, height: '100%', maxHeight: 'none' }}>
                <BasisPills value={landCheckBasis} onChange={setLandCheckBasis} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Land Unit</InputLabel>
          <Select
            label="Land Unit"
            value={landAreaUnit}
            onChange={(e) => setLandAreaUnit(e.target.value as LandAreaUnit)}
          >
            <MenuItem value="bigha">Bigha</MenuItem>
            <MenuItem value="sqYard">Sq Yard</MenuItem>
            <MenuItem value="sqFt">Sq Ft</MenuItem>
            <MenuItem value="sqMeter">Sq Meter</MenuItem>
            <MenuItem value="acre">Acre</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label={activeTab === 'landPlotting' ? 'Value' : 'Rate'}
          value={formatInputNumber(landRate)}
          onChange={(e) => setLandRate(cleanNumberInput(e.target.value))}
          type="text"
          size="small"
          fullWidth
          autoComplete="off"
          inputProps={{ inputMode: 'decimal' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ m: 0, mr: -1.75, height: '100%', maxHeight: 'none' }}>
                <BasisPills value={landRateBasis} onChange={setLandRateBasis} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>{activeTab === 'landPlotting' ? 'Value Unit' : 'Rate Unit'}</InputLabel>
          <Select
            label={activeTab === 'landPlotting' ? 'Value Unit' : 'Rate Unit'}
            value={landRateUnit}
            onChange={(e) => setLandRateUnit(e.target.value as RateUnit)}
          >
            <MenuItem value="bigha">Bigha</MenuItem>
            <MenuItem value="sqYard">Sq Yard</MenuItem>
            <MenuItem value="sqFt">Sq Ft</MenuItem>
            <MenuItem value="sqMeter">Sq Meter</MenuItem>
            <MenuItem value="acre">Acre</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {landAreaUnit === 'bigha' && num(bighaSqMeter) <= 0 && (
        <Alert severity="warning" sx={{ mb: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff8ec', color: DARK }}>
          Add 1 Bigha conversion in Settings to calculate Sq Yard.
        </Alert>
      )}

      {(landOnlyFigures.area712 > 0 || landOnlyFigures.landFigure > 0) && (
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', mb: 2, width: 'fit-content', maxWidth: '100%' }}>
          <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
            <Box component="table" sx={{ borderCollapse: 'collapse', width: 'auto', tableLayout: 'fixed' }}>
              <Box component="colgroup">
                <Box component="col" sx={{ width: { xs: 118, sm: 300 } }} />
                <Box component="col" sx={{ width: { xs: 220, sm: 250 } }} />
                <Box component="col" sx={{ width: { xs: 220, sm: 250 } }} />
              </Box>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ ...headTh, ...stickyFirstColumnSx, textAlign: 'left', bgcolor: PANEL2 }}>Particular</Box>
                  <Box component="th" sx={{ ...headTh, bgcolor: landCheckBasis === '712' ? 'rgba(157,111,50,0.12)' : PANEL2 }}>7/12</Box>
                  <Box component="th" sx={{ ...headTh, bgcolor: landCheckBasis === 'fp' ? 'rgba(157,111,50,0.12)' : PANEL2 }}>FP -{fmtPercent(landOnlyFigures.deduction)}</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {landOnlyFigures.area712 > 0 && (
                  <SnapshotRow
                    label="Land Area"
                    calculation={<CalculationText parts={landCheckBasis === 'fp' ? ['FP', 'to', '7/12'] : [landAreaUnitLabel(landAreaUnit), 'to', 'Sq Yard']} />}
                    values={[
                      <FigureWithUnit value={fmtIN(landOnlyFigures.area712)} unit="Sq Yard" />,
                      <FigureWithUnit value={fmtIN(landOnlyFigures.fpArea)} unit="Sq Yard" />,
                    ]}
                  />
                )}
                {landOnlyFigures.ratePerSqYard712 > 0 && (
                  <SnapshotRow
                    label={activeTab === 'landPlotting' ? 'Value / Sq Yard' : 'Rate / Sq Yard'}
                    calculation={<CalculationText parts={['Land', '/', 'Area']} />}
                    values={[fmtIN(landOnlyFigures.ratePerSqYard712), fmtIN(landOnlyFigures.ratePerSqYardFp)]}
                  />
                )}
                {landOnlyFigures.landFigure > 0 && (
                  <SnapshotRow
                    label="Land"
                    calculation={<CalculationText parts={['Area', 'x', activeTab === 'landPlotting' ? 'Value' : 'Rate']} />}
                    values={[fmtIN(landOnlyFigures.landFigure), fmtIN(landOnlyFigures.landFigure)]}
                    strong
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {activeTab === 'landPlotting' && landOnlyFigures.fpArea > 0 && (
        <>
          <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 16, color: DARK, mb: 1 }}>
            Plotting Saleable
          </Typography>

          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', mb: 2, width: 'fit-content', maxWidth: '100%' }}>
            <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
              <Box component="table" sx={{ borderCollapse: 'collapse', width: 'auto', tableLayout: 'fixed' }}>
                <Box component="colgroup">
                  <Box component="col" sx={{ width: { xs: 118, sm: 300 } }} />
                  {plottingDeductions.map((deduction) => (
                    <Box component="col" key={deduction.id} sx={{ width: { xs: 185, sm: 220 } }} />
                  ))}
                </Box>
                <Box component="thead">
                  <Box component="tr">
                    <Box component="th" sx={{ ...headTh, ...stickyFirstColumnSx, textAlign: 'left', bgcolor: PANEL2 }}>Particular</Box>
                    {plottingDeductions.map((deduction, index) => {
                      const isLast = index === plottingDeductions.length - 1;
                      return (
                      <Box component="th" sx={headTh} key={deduction.id}>
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
                          {deduction.custom ? (
                            <TextField
                              value={formatInputNumber(deduction.value)}
                              onChange={(e) => updatePlottingDeduction(deduction.id, cleanNumberInput(e.target.value))}
                              type="text"
                              size="small"
                              placeholder="%"
                              autoComplete="off"
                              inputProps={{ inputMode: 'decimal' }}
                              sx={{
                                width: 76,
                                '& input': { p: '5px 7px', textAlign: 'right', fontSize: 11, fontWeight: 700 },
                              }}
                            />
                          ) : (
                            <Box component="span">-{fmtPercent(num(deduction.value))}</Box>
                          )}
                          {deduction.custom && (
                            <IconButton
                              size="small"
                              onClick={() => removePlottingDeduction(deduction.id)}
                              sx={{ width: 20, height: 20, color: '#c1372c' }}
                            >
                              <DeleteOutlineIcon fontSize="inherit" />
                            </IconButton>
                          )}
                          {isLast && (
                            <IconButton
                              size="small"
                              onClick={addPlottingDeduction}
                              sx={{ width: 24, height: 24, color: GOLD_D, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}
                            >
                              <AddOutlinedIcon fontSize="inherit" />
                            </IconButton>
                          )}
                      </Stack>
                      </Box>
                      );
                    })}
                  </Box>
                </Box>
                <Box component="tbody">
                  <SnapshotRow
                    label="FP Area"
                    calculation={<CalculationText parts={['Starting', 'area']} />}
                    values={[
                      ...plottingDeductions.map(() => (
                        <FigureWithUnit value={fmtIN(landOnlyFigures.fpArea)} unit="Sq Yard" />
                      )),
                    ]}
                  />
                  <SnapshotRow
                    label="Saleable Yard"
                    calculation={<CalculationText parts={['FP', '-', 'Road/COP']} />}
                    values={[
                      ...plottingDeductions.map((deduction) => (
                        <FigureWithUnit value={fmtIN(landOnlyFigures.fpArea * ((100 - num(deduction.value)) / 100))} unit="Sq Yard" />
                      )),
                    ]}
                    strong
                  />
                  {landOnlyFigures.landFigure > 0 && (
                    <SnapshotRow
                      label="Land / Saleable Yard"
                      calculation={<CalculationText parts={['Land', '/', 'Saleable Yard']} />}
                      values={[
                        ...plottingDeductions.map((deduction) => {
                          const saleable = landOnlyFigures.fpArea * ((100 - num(deduction.value)) / 100);
                          return <FigureWithUnit value={saleable > 0 ? fmtIN(landOnlyFigures.landFigure / saleable) : '0'} unit="/ Sq Yard" />;
                        }),
                      ]}
                    />
                  )}
                  <SnapshotRow
                    label={(
                      <Stack gap={0.75}>
                        <Box component="span">Legal %</Box>
                        <ScenarioNumberField
                          value={plottingLegalPercent}
                          onChange={setPlottingLegalPercent}
                          unit="%"
                          width={{ xs: 86, sm: 100 }}
                        />
                      </Stack>
                    )}
                    calculation={null}
                    values={plottingDeductions.map((deduction) => {
                      const saleable = landOnlyFigures.fpArea * ((100 - num(deduction.value)) / 100);
                      const ratePerSaleable = saleable > 0 ? landOnlyFigures.landFigure / saleable : 0;
                      return <FigureWithUnit value={fmtIN(ratePerSaleable * (num(plottingLegalPercent) / 100))} unit="/ Sq Yard" />;
                    })}
                  />
                  <SnapshotRow
                    label="Development / Sq Yard"
                    calculation={null}
                    values={plottingDeductions.map((deduction) => (
                      <ScenarioNumberField
                        value={plottingScenarioInputs[deduction.id]?.developmentPerSqYard || ''}
                        onChange={(value) => updatePlottingScenarioInput(deduction.id, 'developmentPerSqYard', value)}
                        unit="/ Sq Yard"
                      />
                    ))}
                  />
                  {landOnlyFigures.landFigure > 0 && (
                    <SnapshotRow
                      label="Base / Saleable Yard"
                      calculation={<CalculationText parts={['Land / Saleable Yard', '+', 'Legal %', '+', 'Development / Sq Yard']} />}
                      values={plottingDeductions.map((deduction) => {
                        const saleable = landOnlyFigures.fpArea * ((100 - num(deduction.value)) / 100);
                        const inputs = plottingScenarioInputs[deduction.id];
                        const ratePerSaleable = saleable > 0 ? landOnlyFigures.landFigure / saleable : 0;
                        const legalPerSaleable = ratePerSaleable * (num(plottingLegalPercent) / 100);
                        const developmentPerSaleable = num(inputs?.developmentPerSqYard || '');
                        return <FigureWithUnit value={saleable > 0 ? fmtIN(ratePerSaleable + legalPerSaleable + developmentPerSaleable) : '0'} unit="/ Sq Yard" />;
                      })}
                      strong
                    />
                  )}
                  <SnapshotRow
                    label="Saleable Value / Sq Yard"
                    calculation={null}
                    values={plottingDeductions.map((deduction) => (
                      <ScenarioNumberField
                        value={plottingScenarioInputs[deduction.id]?.saleableRatePerSqYard || ''}
                        onChange={(value) => updatePlottingScenarioInput(deduction.id, 'saleableRatePerSqYard', value)}
                        unit="/ Sq Yard"
                      />
                    ))}
                  />
                  <SnapshotRow
                    label="Saleable Figure"
                    calculation={<CalculationText parts={['Saleable Yard', 'x', 'Saleable Value']} />}
                    values={plottingDeductions.map((deduction) => {
                      const saleable = landOnlyFigures.fpArea * ((100 - num(deduction.value)) / 100);
                      return fmtIN(saleable * num(plottingScenarioInputs[deduction.id]?.saleableRatePerSqYard || ''));
                    })}
                  />
                  {landOnlyFigures.landFigure > 0 && (
                    <>
                      <SnapshotRow
                        label="Net Figure"
                        calculation={<CalculationText parts={['Saleable Figure', '-', 'Base / Saleable Yard']} />}
                        values={plottingDeductions.map((deduction) => {
                          const saleable = landOnlyFigures.fpArea * ((100 - num(deduction.value)) / 100);
                          const inputs = plottingScenarioInputs[deduction.id];
                          const ratePerSaleable = saleable > 0 ? landOnlyFigures.landFigure / saleable : 0;
                          const legalPerSaleable = ratePerSaleable * (num(plottingLegalPercent) / 100);
                          const developmentPerSaleable = num(inputs?.developmentPerSqYard || '');
                          const costFigure = saleable * (ratePerSaleable + legalPerSaleable + developmentPerSaleable);
                          const saleableFigure = saleable * num(inputs?.saleableRatePerSqYard || '');
                          return fmtIN(saleableFigure - costFigure);
                        })}
                        strong
                      />
                      <SnapshotRow
                        label="Net / Land"
                        calculation={<CalculationText parts={['Net Figure', '/', 'Land']} />}
                        values={plottingDeductions.map((deduction) => {
                          const saleable = landOnlyFigures.fpArea * ((100 - num(deduction.value)) / 100);
                          const inputs = plottingScenarioInputs[deduction.id];
                          const ratePerSaleable = saleable > 0 ? landOnlyFigures.landFigure / saleable : 0;
                          const legalPerSaleable = ratePerSaleable * (num(plottingLegalPercent) / 100);
                          const developmentPerSaleable = num(inputs?.developmentPerSqYard || '');
                          const costFigure = saleable * (ratePerSaleable + legalPerSaleable + developmentPerSaleable);
                          const saleableFigure = saleable * num(inputs?.saleableRatePerSqYard || '');
                          const net = saleableFigure - costFigure;
                          return fmtPercent((net / landOnlyFigures.landFigure) * 100);
                        })}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}

      <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 16, color: DARK, mb: 1 }}>
        Payment Schedule
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: 1.5, mb: 2 }}>
        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          size="small"
          fullWidth
          autoComplete="off"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Terms"
          value={formatInputNumber(terms)}
          onChange={(e) => setTerms(cleanNumberInput(e.target.value))}
          type="text"
          size="small"
          fullWidth
          autoComplete="off"
          inputProps={{ inputMode: 'decimal' }}
          InputProps={{ endAdornment: <InputAdornment position="end">months</InputAdornment> }}
        />
        <TextField
          label="Installment"
          value={formatInputNumber(installments)}
          onChange={(e) => setInstallments(cleanNumberInput(e.target.value))}
          type="text"
          size="small"
          fullWidth
          autoComplete="off"
          inputProps={{ inputMode: 'decimal' }}
        />
      </Box>

      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} mb={2}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: FAINT, textTransform: 'uppercase', letterSpacing: '.5px' }}>
          Spacing
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={scheduleMode}
          onChange={(_, value) => value && setScheduleMode(value)}
        >
          <ToggleButton value="afterFirst" sx={compactToggleSx}>After 1st</ToggleButton>
          <ToggleButton value="fromStart" sx={compactToggleSx}>From Start</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {landOnlySchedule.warning && (
        <Alert severity="warning" sx={{ mb: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff8ec', color: DARK }}>
          {landOnlySchedule.warning}
        </Alert>
      )}

      {landOnlySchedule.rows.length > 0 && (
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 680 }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Installment</Box>
                  <Box component="th" sx={headTh}>Installment Date</Box>
                  <Box component="th" sx={headTh}>Installment %</Box>
                  <Box component="th" sx={headTh}>Installment</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {landOnlySchedule.rows.map((row) => (
                  <Box component="tr" key={row.installment} sx={{ '&:hover td': { bgcolor: 'rgba(157,111,50,0.08)' } }}>
                    <Box component="td" sx={{ ...bodyTd, textAlign: 'left', fontWeight: 600 }}>{row.installment}</Box>
                    <Box component="td" sx={bodyTd}>{formatDate(row.date)}</Box>
                    <Box component="td" sx={bodyTd}>
                      <TextField
                        value={formatInputNumber(installmentPercents[row.installment - 1] ?? '')}
                        onChange={(e) => {
                          const value = cleanNumberInput(e.target.value);
                          setInstallmentPercents((prev) => prev.map((pct, index) => (
                            index === row.installment - 1 ? value : pct
                          )));
                        }}
                        type="text"
                        size="small"
                        autoComplete="off"
                        inputProps={{ inputMode: 'decimal' }}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        sx={{ width: 110 }}
                      />
                    </Box>
                    <Box component="td" sx={{ ...bodyTd, fontWeight: 700, color: GOLD_D }}>{fmtIN(row.amount)}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, fontFamily: FONT_BODY, maxWidth: 1360, mx: 'auto', width: '100%' }}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
        <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 22, color: DARK, flexGrow: 1 }}>
          Feasibility
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} mb={1.5}>
        <ToggleButtonGroup exclusive size="small" value={activeTab} onChange={(_, value) => value && setActiveTab(value)}>
          <ToggleButton value="landBuilding" sx={toggleSx}>Land + Building</ToggleButton>
          <ToggleButton value="landPlotting" sx={toggleSx}>Land + Plotting</ToggleButton>
          <ToggleButton value="paymentSchedule" sx={toggleSx}>Payment Schedule</ToggleButton>
          <ToggleButton value="landOnly" sx={toggleSx}>Land Only</ToggleButton>
          <ToggleButton value="settings" sx={toggleSx}>Settings</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {activeTab === 'settings' ? (
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}`, bgcolor: PANEL2 }}>
            <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 18, color: DARK }}>
              Settings
            </Typography>
            <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
              Only conversion settings are saved. Feasibility inputs are never stored.
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                label="1 Bigha"
                value={formatInputNumber(bighaSqMeterDraft)}
                onChange={(e) => setBighaSqMeterDraft(cleanNumberInput(e.target.value))}
                type="text"
                size="small"
                autoComplete="off"
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{ endAdornment: <InputAdornment position="end">Sq Meter</InputAdornment> }}
                sx={{ maxWidth: { sm: 320 } }}
                fullWidth
              />
              <TextField
                label="FP Deduction"
                value={formatInputNumber(fpDeductionDraft)}
                onChange={(e) => setFpDeductionDraft(cleanNumberInput(e.target.value))}
                type="text"
                size="small"
                autoComplete="off"
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ maxWidth: { sm: 220 } }}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={saveConversionSettings}
                disabled={settingsSaving}
                sx={{ minWidth: 110 }}
              >
                Save
              </Button>
            </Stack>
            <Typography sx={{ fontSize: 12, color: MUTED, mt: 1.25 }}>
              Sq Meter to Sq Yard uses fixed conversion: 1 Sq Meter = 1.1959900463 Sq Yard.
            </Typography>
            {num(bighaSqMeterDraft) > 0 && (
              <Typography sx={{ fontSize: 12, color: GOLD_D, fontWeight: 700, mt: 0.75 }}>
                1 Bigha = {fmtIN(num(bighaSqMeterDraft) * SQ_METER_TO_SQ_YARD)} Sq Yard
              </Typography>
            )}
            {settingsStatus && (
              <Alert severity={settingsStatus.includes('saved') ? 'success' : 'warning'} sx={{ mt: 2, border: `1px solid ${BORDER}` }}>
                {settingsStatus}
              </Alert>
            )}
          </Box>
        </Box>
      ) : activeTab === 'landBuilding' ? (
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}`, bgcolor: PANEL2 }}>
            <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 18, color: DARK }}>
              Land + Building
            </Typography>
            <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
              Temporary calculator. Values reset when this page is refreshed.
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            {landBuildingInputs}
          </Box>
        </Box>
      ) : activeTab === 'landOnly' ? (
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}`, bgcolor: PANEL2 }}>
            <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 18, color: DARK }}>
              Land Only
            </Typography>
            <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
              Temporary calculator. Values reset when this page is refreshed.
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            {landOnlyInputs}
          </Box>
        </Box>
      ) : activeTab === 'landPlotting' ? (
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}`, bgcolor: PANEL2 }}>
            <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 18, color: DARK }}>
              Land + Plotting
            </Typography>
            <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
              Temporary calculator. Values reset when this page is refreshed.
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            {landOnlyInputs}
          </Box>
        </Box>
      ) : (
      <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}`, bgcolor: PANEL2 }}>
          <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 18, color: DARK }}>
            Payment Schedule
          </Typography>
          <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
            Temporary calculator. Values reset when this page is refreshed.
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          {paymentInputs}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: 1.5, mb: 2 }}>
            <Kpi label="Total" value={fmtIN(schedule.totalAmount)} />
            <Kpi label="Converted Area" value={`${fmtIN(convertedSqYard)} Sq Yard`} />
            <Kpi label="Installments" value={schedule.installments > 0 ? String(schedule.installments) : '—'} />
            <Kpi label="Terms" value={schedule.terms > 0 ? `${schedule.terms} months` : '—'} />
          </Box>

          {schedule.warning && (
            <Alert severity="warning" sx={{ mb: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff8ec', color: DARK }}>
              {schedule.warning}
            </Alert>
          )}

          {schedule.rows.length > 0 && (
            <>
              <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 16, color: DARK, mb: 1 }}>
                Installment after calculating
              </Typography>

              <Stack direction="row" justifyContent="flex-end" mb={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddOutlinedIcon />}
                  onClick={addToCompare}
                  sx={{ borderColor: BORDER, color: MUTED }}
                >
                  Add to Compare
                </Button>
              </Stack>

              <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 680 }}>
                    <Box component="thead">
                      <Box component="tr">
                        <Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Installment</Box>
                        <Box component="th" sx={headTh}>Installment Date</Box>
                        <Box component="th" sx={headTh}>Installment %</Box>
                        <Box component="th" sx={headTh}>Installment</Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {schedule.rows.map((row) => (
                        <Box component="tr" key={row.installment} sx={{ '&:hover td': { bgcolor: 'rgba(157,111,50,0.08)' } }}>
                          <Box component="td" sx={{ ...bodyTd, textAlign: 'left', fontWeight: 600 }}>{row.installment}</Box>
                          <Box component="td" sx={bodyTd}>{formatDate(row.date)}</Box>
                          <Box component="td" sx={bodyTd}>
                            <TextField
                              value={formatInputNumber(installmentPercents[row.installment - 1] ?? '')}
                              onChange={(e) => {
                                const value = cleanNumberInput(e.target.value);
                                setInstallmentPercents((prev) => prev.map((pct, index) => (
                                  index === row.installment - 1 ? value : pct
                                )));
                              }}
                              type="text"
                              size="small"
                              autoComplete="off"
                              inputProps={{ inputMode: 'decimal' }}
                              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                              sx={{ width: 110 }}
                            />
                          </Box>
                          <Box component="td" sx={{ ...bodyTd, fontWeight: 700, color: GOLD_D }}>{fmtIN(row.amount)}</Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {compareScenarios.length > 0 && (
            <Box sx={{ mt: 2.5, border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: PANEL2 }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: 16, color: DARK }}>
                      Compare Terms
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.25 }}>
                      Add current calculation to compare. Rows reset on refresh.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setCompareScenarios([])}
                    sx={{ borderColor: BORDER, color: MUTED }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 1040 }}>
                  <Box component="thead">
                    <Box component="tr">
                      <Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Scenario</Box>
                      <Box component="th" sx={headTh}>Terms</Box>
                      <Box component="th" sx={headTh}>Count</Box>
                      <Box component="th" sx={headTh}>First Date</Box>
                      <Box component="th" sx={headTh}>Last Date</Box>
                      <Box component="th" sx={headTh}>Installment %</Box>
                      <Box component="th" sx={headTh}>Total</Box>
                      <Box component="th" sx={headTh}>Highest</Box>
                      <Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Installment</Box>
                      <Box component="th" sx={{ ...headTh, textAlign: 'left' }}>Payment Dates</Box>
                      <Box component="th" sx={headTh} />
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {compareScenarios.map((scenario) => {
                      const rows = scenario.rows;
                      const first = rows[0]?.date || null;
                      const last = rows[rows.length - 1]?.date || null;
                      const maxAmount = rows.reduce((max, row) => Math.max(max, row.amount), 0);
                      return (
                        <Box component="tr" key={scenario.id} sx={{ '&:hover td': { bgcolor: 'rgba(157,111,50,0.08)' } }}>
                          <Box component="td" sx={{ ...bodyTd, textAlign: 'left', fontWeight: 700 }}>{scenario.label}</Box>
                          <Box component="td" sx={bodyTd}>{scenario.terms} months</Box>
                          <Box component="td" sx={bodyTd}>{scenario.installments}</Box>
                          <Box component="td" sx={bodyTd}>{first ? formatDate(first) : '-'}</Box>
                          <Box component="td" sx={bodyTd}>{last ? formatDate(last) : '-'}</Box>
                          <Box component="td" sx={bodyTd}>{rows.length ? formatPercentList(rows) : '-'}</Box>
                          <Box component="td" sx={{ ...bodyTd, fontWeight: 700 }}>{fmtIN(scenario.total)}</Box>
                          <Box component="td" sx={{ ...bodyTd, fontWeight: 700, color: GOLD_D }}>{maxAmount ? fmtIN(maxAmount) : '-'}</Box>
                          <Box component="td" sx={{ ...bodyTd, textAlign: 'left', color: GOLD_D, whiteSpace: 'normal', minWidth: 150, fontWeight: 700 }}>
                            {rows.length ? rows.map((row) => (
                              <Box key={row.installment}>{fmtIN(row.amount)}</Box>
                            )) : '-'}
                          </Box>
                          <Box component="td" sx={{ ...bodyTd, textAlign: 'left', color: MUTED, whiteSpace: 'normal', minWidth: 150 }}>
                            {rows.length ? rows.map((row) => (
                              <Box key={row.installment}>{formatDate(row.date)}</Box>
                            )) : '-'}
                          </Box>
                          <Box component="td" sx={bodyTd}>
                            <IconButton
                              size="small"
                              onClick={() => setCompareScenarios((prev) => prev.filter((row) => row.id !== scenario.id))}
                              sx={{ width: 24, height: 24, color: '#c1372c' }}
                            >
                              <DeleteOutlineIcon fontSize="inherit" />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      )}
    </Box>
  );
}

type PaymentScheduleInput = {
  date: string;
  terms: number;
  installments: number;
  landArea: number;
  multiplier: number;
  scheduleMode: ScheduleMode;
  percents?: number[];
};

type PaymentScheduleRow = {
  installment: number;
  date: Date;
  percent: number;
  amount: number;
};

function buildPaymentSchedule(input: PaymentScheduleInput): {
  rows: PaymentScheduleRow[];
  totalAmount: number;
  installments: number;
  terms: number;
  warning: string;
} {
  const totalAmount = input.landArea * input.multiplier;
  const installments = input.installments > 0 ? input.installments : 0;
  const terms = input.terms > 0 ? input.terms : 0;
  const startDate = parseDate(input.date);

  if (!startDate || !terms || !installments) {
    return { rows: [], totalAmount, installments, terms, warning: '' };
  }

  const firstDate = addDays(startDate, 60);
  const lastDate = addMonths(startDate, terms);
  const percents = normalizePercents(input.installments, input.percents);
  const warnings: string[] = [];
  if (installments > 1 && lastDate <= firstDate) {
    warnings.push('Terms date is before or same as the first installment date. Calculation is still shown based on the entered terms.');
  }
  if (round2(percents.reduce((sum, pct) => sum + pct, 0)) !== 100) {
    warnings.push('Installment percentages do not total 100%.');
  }

  const rows: PaymentScheduleRow[] = [];
  for (let index = 0; index < installments; index += 1) {
    const installment = index + 1;
    const percent = percents[index] || 0;
    const rowDate = getInstallmentDate({
      index,
      installments,
      terms,
      startDate,
      firstDate,
      lastDate,
      scheduleMode: input.scheduleMode,
    });
    rows.push({
      installment,
      date: rowDate,
      percent,
      amount: totalAmount * (percent / 100),
    });
  }

  if (input.scheduleMode === 'fromStart') {
    const outOfOrder = rows.some((row, index) => index > 0 && row.date < rows[index - 1].date);
    if (outOfOrder) {
      warnings.push('From Start Date can place a later installment before an earlier installment when terms are short.');
    }
  }

  return { rows, totalAmount, installments, terms, warning: warnings.join(' ') };
}

function getInstallmentDate(input: {
  index: number;
  installments: number;
  terms: number;
  startDate: Date;
  firstDate: Date;
  lastDate: Date;
  scheduleMode: ScheduleMode;
}): Date {
  const { index, installments, terms, startDate, firstDate, lastDate, scheduleMode } = input;
  if (installments === 1) return lastDate;
  if (index === 0) return firstDate;
  if (index === installments - 1) return lastDate;
  if (scheduleMode === 'fromStart') return interpolateDate(startDate, lastDate, index / (installments - 1));
  const firstInstallmentOffsetMonths = 2;
  const gapMonths = roundToNearestHalf((terms - firstInstallmentOffsetMonths) / (installments - 1));
  return addDecimalMonths(firstDate, gapMonths * index);
}

const headTh: any = {
  bgcolor: PANEL2,
  fontSize: 10.5,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.6px',
  color: FAINT,
  textAlign: 'right',
  p: '9px 12px',
  borderBottom: `1px solid ${BORDER}`,
  whiteSpace: 'nowrap',
  fontFamily: FONT_BODY,
};

const bodyTd: any = {
  p: { xs: '9px 8px', sm: '10px 12px' },
  borderBottom: `1px solid ${BORDER}`,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  fontFamily: FONT_BODY,
  fontSize: 13,
  color: DARK,
};

const stickyFirstColumnSx: any = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  boxShadow: `1px 0 0 ${BORDER}`,
};

function SnapshotSectionRow({ label, colSpan }: { label: string; colSpan: number }) {
  const fillColSpan = Math.max(colSpan - 1, 1);
  return (
    <Box component="tr">
      <Box
        component="td"
        sx={{
          ...stickyFirstColumnSx,
          bgcolor: '#ebe6dc',
          color: FAINT,
          borderBottom: `1px solid ${BORDER}`,
          p: { xs: '7px 8px', sm: '8px 12px' },
          fontFamily: FONT_BODY,
          fontSize: { xs: 10, sm: 10.5 },
          fontWeight: 800,
          letterSpacing: '.7px',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          textAlign: 'left',
          zIndex: 3,
        }}
      >
        {label}
      </Box>
      <Box
        component="td"
        colSpan={fillColSpan}
        sx={{
          bgcolor: '#ebe6dc',
          borderBottom: `1px solid ${BORDER}`,
          p: { xs: '7px 8px', sm: '8px 12px' },
        }}
      />
    </Box>
  );
}

function SnapshotRow({
  label,
  calculation,
  values,
  onClick,
  strong,
  sticky,
  stickyActive,
}: {
  label: React.ReactNode;
  calculation: React.ReactNode;
  values: React.ReactNode[];
  onClick?: () => void;
  strong?: boolean;
  sticky?: boolean;
  stickyActive?: boolean;
}) {
  return (
    <Box component="tr" sx={{ '&:hover td': { bgcolor: onClick ? 'rgba(157,111,50,0.08)' : undefined } }}>
      <Box
        component="td"
        sx={{
          ...bodyTd,
          ...stickyFirstColumnSx,
          textAlign: 'left',
          color: strong ? GOLD_D : DARK,
          fontWeight: strong ? 700 : 600,
          whiteSpace: 'normal',
          minWidth: { xs: 118, sm: 210 },
          width: { xs: 118, sm: 300 },
          maxWidth: { xs: 118, sm: 300 },
          fontSize: { xs: 11.5, sm: 13 },
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          bgcolor: sticky && stickyActive ? '#fbf7ef' : '#fff',
          top: sticky ? 0 : undefined,
          zIndex: sticky ? 5 : stickyFirstColumnSx.zIndex,
        }}
      >
        <Box component="span">{label}</Box>
        {calculation && (
          <Box sx={{ display: { xs: 'none', sm: 'block' }, mt: 0.35, color: MUTED, fontSize: 11.5, fontWeight: 500, lineHeight: 1.25 }}>
            {calculation}
          </Box>
        )}
      </Box>
      {values.map((value, index) => {
        const clickable = index === 0 && Boolean(onClick);
        return (
          <Box
            component="td"
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            onClick={clickable ? onClick : undefined}
            sx={{
              ...bodyTd,
              ...(sticky ? {
                position: 'sticky',
                top: 0,
                zIndex: 4,
                bgcolor: stickyActive ? '#fbf7ef' : '#fff',
                boxShadow: `0 1px 0 ${BORDER}`,
              } : {}),
              color: strong ? GOLD_D : clickable ? GOLD_D : DARK,
              fontWeight: strong || clickable ? 700 : 500,
              cursor: clickable ? 'pointer' : 'default',
            }}
          >
            {value}
          </Box>
        );
      })}
    </Box>
  );
}

function CalculationText({ parts }: { parts: React.ReactNode[] }) {
  return (
    <Stack component="span" direction="row" alignItems="center" flexWrap="wrap" gap={0.5}>
      {parts.map((part, index) => (
        <Box
          component="span"
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          sx={{ color: part === 'x' || part === '+' || part === '-' || part === '/' ? FAINT : MUTED }}
        >
          {part}
        </Box>
      ))}
    </Stack>
  );
}

function FigureWithUnit({ value, unit }: { value: string; unit: string }) {
  return (
    <Stack component="span" direction="row" alignItems="baseline" justifyContent="flex-end" gap={0.5}>
      <Box component="span">{value}</Box>
      <Box component="span" sx={{ color: MUTED, fontSize: 10.5, fontWeight: 500 }}>
        {unit}
      </Box>
    </Stack>
  );
}

function LoadedScenarioValue({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
        minHeight: 34,
        border: 0,
        p: 0,
        bgcolor: 'transparent',
        color: DARK,
        cursor: 'pointer',
        fontFamily: FONT_BODY,
        fontSize: { xs: 13, sm: 13 },
        fontWeight: 500,
        fontVariantNumeric: 'tabular-nums',
        textAlign: 'right',
        '&:hover': { color: GOLD_D },
      }}
    >
      {children}
    </Box>
  );
}

function handleBuildingScenarioInputTab(
  event: React.KeyboardEvent<HTMLInputElement>,
  scenarioIndex?: number,
  rowOrder?: number
) {
  if (event.key !== 'Tab' || scenarioIndex === undefined || rowOrder === undefined) return;
  const inputs = Array.from(document.querySelectorAll<HTMLInputElement>('input[data-building-scenario-input="true"]'))
    .sort((a, b) => {
      const scenarioA = Number(a.dataset.scenarioIndex || 0);
      const scenarioB = Number(b.dataset.scenarioIndex || 0);
      const rowA = Number(a.dataset.rowOrder || 0);
      const rowB = Number(b.dataset.rowOrder || 0);
      return scenarioA === scenarioB ? rowA - rowB : scenarioA - scenarioB;
    });
  const currentIndex = inputs.indexOf(event.currentTarget);
  if (currentIndex < 0) return;
  const nextIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
  const nextInput = inputs[nextIndex];
  if (!nextInput) return;
  event.preventDefault();
  nextInput.focus();
  nextInput.select();
}

function ScenarioNameSearchField({
  value,
  onChange,
  options,
  onSearch,
  onSelect,
  scenarioIndex,
  rowOrder,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SavedBuildingScenario[];
  onSearch: (value: string) => void;
  onSelect: (saved: SavedBuildingScenario) => void;
  scenarioIndex?: number;
  rowOrder?: number;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Autocomplete<SavedBuildingScenario, false, false, true>
      freeSolo
      size="small"
      options={options}
      loading={false}
      open={open && options.length > 0}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      inputValue={value}
      value={null}
      filterOptions={(items) => items}
      getOptionLabel={(option) => (
        typeof option === 'string'
          ? option
          : `${option.name} | TP ${option.tpNumber} | FP ${option.fpNumber}`
      )}
      onInputChange={(_, nextValue, reason) => {
        if (reason === 'reset') return;
        onChange(nextValue);
        onSearch(nextValue);
      }}
      onChange={(_, selected) => {
        if (selected && typeof selected !== 'string') onSelect(selected);
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Stack sx={{ minWidth: 0 }}>
            <Box component="span" sx={{ fontSize: 12.5, fontWeight: 700, color: DARK, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {option.name}
            </Box>
            <Box component="span" sx={{ fontSize: 10.5, color: MUTED }}>
              TP {option.tpNumber} | FP {option.fpNumber}
            </Box>
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          onFocus={() => setOpen(true)}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleBuildingScenarioInputTab(event, scenarioIndex, rowOrder)}
          autoComplete="off"
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off',
            'data-building-scenario-input': scenarioIndex === undefined ? undefined : 'true',
            'data-scenario-index': scenarioIndex,
            'data-row-order': rowOrder,
          }}
        />
      )}
      sx={{
        width: { xs: 116, sm: 132 },
        ml: 'auto',
        '& .MuiOutlinedInput-root': {
          height: 34,
          minHeight: 34,
          p: 0,
          pr: '0 !important',
          alignItems: 'center',
        },
        '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
          p: '6px 7px !important',
          textAlign: 'right !important',
          fontSize: { xs: 12, sm: 12.5 },
        },
        '& input': { textAlign: 'right !important' },
        '& .MuiAutocomplete-endAdornment': { display: 'none' },
      }}
    />
  );
}

function ScenarioNumberField({
  value,
  onChange,
  unit,
  scenarioIndex,
  rowOrder,
  width,
}: {
  value: string;
  onChange: (value: string) => void;
  unit?: string;
  scenarioIndex?: number;
  rowOrder?: number;
  width?: any;
}) {
  return (
    <TextField
      value={formatInputNumber(value)}
      onChange={(e) => onChange(cleanNumberInput(e.target.value))}
      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleBuildingScenarioInputTab(event, scenarioIndex, rowOrder)}
      type="text"
      size="small"
      autoComplete="off"
      inputProps={{
        inputMode: 'decimal',
        'data-building-scenario-input': scenarioIndex === undefined ? undefined : 'true',
        'data-scenario-index': scenarioIndex,
        'data-row-order': rowOrder,
      }}
      InputProps={unit ? {
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{ '& .MuiTypography-root': { fontSize: { xs: 11, sm: 13 }, color: MUTED } }}
          >
            {unit}
          </InputAdornment>
        ),
      } : undefined}
      sx={{
        width: width || { xs: 116, sm: 132 },
        '& input': { p: '6px 7px', textAlign: 'right', fontSize: { xs: 12, sm: 12.5 } },
      }}
    />
  );
}

function BasisPills({ value, onChange }: { value: LandCheckBasis; onChange: (value: LandCheckBasis) => void }) {
  return (
    <Stack
      direction="row"
      component="span"
      sx={{
        alignSelf: 'stretch',
        borderLeft: `1px solid ${BORDER}`,
        overflow: 'hidden',
        bgcolor: '#fff',
        mr: 0,
      }}
    >
      {([
        ['712', '7/12'],
        ['fp', 'FP'],
      ] as [LandCheckBasis, string][]).map(([option, label]) => {
        const selected = value === option;
        return (
          <Box
            component="button"
            type="button"
            key={option}
            onClick={() => onChange(option)}
            sx={{
              border: 0,
              borderRight: option === '712' ? `1px solid ${BORDER}` : 0,
              bgcolor: selected ? GOLD : '#fff',
              color: selected ? '#fff' : MUTED,
              cursor: 'pointer',
              fontFamily: FONT_BODY,
              fontSize: { xs: 10, sm: 10.5 },
              fontWeight: 800,
              lineHeight: 1.2,
              minWidth: { xs: 34, sm: 38 },
              px: { xs: 0.55, sm: 0.75 },
              py: 0,
              '&:hover': { bgcolor: selected ? GOLD_D : 'rgba(157,111,50,0.08)' },
            }}
          >
            {label}
          </Box>
        );
      })}
    </Stack>
  );
}

function Kpi({ label, value, action, onClick }: { label: string; value: string; action?: React.ReactNode; onClick?: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: 2.5,
        p: 1.75,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { bgcolor: 'rgba(157,111,50,0.08)', borderColor: GOLD } : undefined,
      }}
    >
      <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.7px', color: FAINT, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 22, fontWeight: 600, color: DARK, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {action}
    </Box>
  );
}

function parseDate(value: string): Date | null {
  if (!value) return null;
  const parts = value.split('-').map((x) => Number(x));
  if (parts.length !== 3 || parts.some((x) => !Number.isFinite(x))) return null;
  const [year, month, day] = parts;
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  const day = next.getDate();
  next.setMonth(next.getMonth() + months);
  if (next.getDate() < day) next.setDate(0);
  return next;
}

function addDecimalMonths(date: Date, months: number): Date {
  const wholeMonths = Math.trunc(months);
  const fraction = months - wholeMonths;
  const next = addMonths(date, wholeMonths);
  if (Math.abs(fraction - 0.5) < 0.001) return addDays(next, 15);
  return next;
}

function interpolateDate(start: Date, end: Date, ratio: number): Date {
  const time = start.getTime() + ((end.getTime() - start.getTime()) * ratio);
  const next = new Date(time);
  next.setHours(0, 0, 0, 0);
  return next;
}

function formatDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function formatPercentList(rows: PaymentScheduleRow[]): string {
  return rows.map((row) => `${row.percent}%`).join(', ');
}

function formatInputDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function fmtIN(n: number): string {
  const neg = n < 0;
  let s = String(Math.round(Math.abs(n)));
  if (s.length > 3) {
    const last = s.slice(-3);
    const rest = s.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    s = `${rest},${last}`;
  }
  return `${neg ? '-' : ''}${s}`;
}

function fmtPercent(n: number): string {
  if (!Number.isFinite(n)) return '0%';
  const rounded = Math.round(n * 10) / 10;
  return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
}

function num(value: string | number): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = Number(value.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function cleanNumberInput(value: string): string {
  const withoutCommas = value.replace(/,/g, '');
  const cleaned = withoutCommas.replace(/[^\d.]/g, '');
  const [whole, ...decimalParts] = cleaned.split('.');
  return decimalParts.length ? `${whole}.${decimalParts.join('')}` : whole;
}

function formatInputNumber(value: string): string {
  if (!value) return '';
  const cleaned = cleanNumberInput(value);
  if (!cleaned) return '';
  const hasTrailingDot = cleaned.endsWith('.');
  const [whole, decimal] = cleaned.split('.');
  const formattedWhole = fmtIN(num(whole));
  if (decimal !== undefined) return `${formattedWhole}.${decimal}`;
  return hasTrailingDot ? `${formattedWhole}.` : formattedWhole;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function roundToNearestHalf(value: number): number {
  return Math.round(value * 2) / 2;
}

function defaultPercentInputs(count: number): string[] {
  if (count <= 0) return [];
  const base = Math.floor(100 / count);
  return Array.from({ length: count }, (_, index) => (
    index === count - 1 ? String(100 - (base * (count - 1))) : String(base)
  ));
}

function normalizePercents(count: number, percents?: number[]): number[] {
  if (count <= 0) return [];
  if (!percents || percents.length !== count) return defaultPercentInputs(count).map(num);
  return percents.map((pct) => (Number.isFinite(pct) ? pct : 0));
}

function convertToSqYard(area: number, unit: LandAreaUnit, bighaSizeSqMeter: number): number {
  if (!area) return 0;
  if (unit === 'sqYard') return area;
  if (unit === 'sqFt') return area / 9;
  if (unit === 'sqMeter') return area * SQ_METER_TO_SQ_YARD;
  if (unit === 'acre') return area * 4840;
  if (unit === 'bigha') return area * bighaSizeSqMeter * SQ_METER_TO_SQ_YARD;
  return area;
}

function getUnitSqYard(unit: LandAreaUnit, bighaSizeSqMeter: number): number {
  return convertToSqYard(1, unit, bighaSizeSqMeter);
}

function landAreaUnitLabel(unit: LandAreaUnit): string {
  if (unit === 'sqYard') return 'Sq Yard';
  if (unit === 'sqFt') return 'Sq Ft';
  if (unit === 'sqMeter') return 'Sq Meter';
  if (unit === 'acre') return 'Acre';
  if (unit === 'bigha') return 'Bigha';
  return 'Area';
}
